import { css } from 'aphrodite';
import * as React from 'react';
import { useMediaQuery } from 'react-responsive';
import type { ISupportCategory } from '../../../models/Support/ISupportCategory';
import type { ISupportTicket } from '../../../models/Support/ISupportTicket';
import { TicketStatus } from '../../../models/Support/SupportEnums';
import type { InboxNavItem } from '../../../queries/Support/useSupportInboxNav';
import type { ISupportAgentConfig } from '../../../queries/Support/useSupportTeam';
import { usePrefetchSupportTicket } from '../../../queries/Support/useSupportTicketQuery';
import { useBulkUpdateTicketsMutation } from '../../../queries/Support/useSupportTicketMutations';
import { getApiErrorMessage, getInitials, getSupportContactProfilePicSrc } from '../../../utils/Support/supportHelpers';
import { Avatar } from '@HoudiniUI/Avatar';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { ChevronDown } from '../../../web/components/svgs/icons/ChevronDown';
import { ChevronLeft } from '../../../web/components/svgs/icons/ChevronLeft';
import { PlusIconThin } from '../../../web/components/svgs/icons/PlusIconThin';
import { Button } from '../../../web/components-v2/Button';
import { Dropdown, DropdownContent, DropdownItem, DropdownTrigger } from '../../../web/components-v2/Dropdown';
import { Pill } from '../../../web/components-v2/Pill';
import { TicketChannelBadge } from '../../components/supportShared/TicketChannelBadge';
import type { NewTicketMode } from '../../components/supportShared/NewTicketModal/newTicketDraft';
import { NewTicketMenu } from '../../components/supportShared/NewTicketMenu';
import type { SupportAgentTicketCountMap } from '../../../queries/Support/useSupportAgentTicketCounts';
import { countActiveInboxFilters, type InboxFilters, prepareInboxFilterChoices } from './inboxFilters';
import { SearchGlyph, SidebarGlyph } from './inboxIcons';
import { inboxStyles as s } from './styles/inboxStyles';
import {
	formatRelativeTime,
	getStatusInfo,
	getStatusPillLabel,
	getTicketContactLabel,
	isDimmedTicketStatus,
} from './supportTicketPresentation';
import { TicketPagination } from './TicketPagination';
import { TicketSearchBar } from './TicketSearchBar';
import { useTicketListPeek } from './useTicketListPeek';

/** Views that hold tickets off the agent's plate; every other view counts what is still open. */
const INACTIVE_VIEW_COUNT_NOUNS: Record<string, string> = { closed: 'closed', spam: 'in spam', archive: 'archived' };

/** The list of tickets plus its pagination, bundled so the pane takes one source instead of eight props. */
export interface TicketListSource {
	tickets: ISupportTicket[];
	isLoading: boolean;
	isError: boolean;
	totalCount: number;
	page: number;
	totalPages: number;
	onSelectPage: (page: number) => void;
}

interface TicketListPaneProps {
	/** Tickets + pagination for the active view (filter view or search results). */
	list: TicketListSource;
	viewLabel: string;
	onBack: () => void;
	/** Renders the phone new-ticket action. Omitted when the user is not an agent in this mailbox. */
	onNewTicket?: (mode: NewTicketMode) => void;
	selectedTicketId?: string;
	onSelectTicket: (id: string) => void;
	navItems: InboxNavItem[];
	categories: ISupportCategory[];
	activeFilter: string;
	onSelectFilter: (key: string) => void;
	/** Active while a search query is running — swaps the list to results and adjusts the count copy. */
	isSearching: boolean;
	searchValue: string;
	onSearchChange: (value: string) => void;
	/** Structured Status/Priority/Category/Assignee/Sort refinement from the Search criteria popover. */
	filters: InboxFilters;
	onFiltersChange: (next: InboxFilters) => void;
	onClearFilters: () => void;
	/** Mailbox agents, used to populate the Assignee filter. */
	teamMembers: ReadonlyArray<Pick<ISupportAgentConfig, 'agentId' | 'agentName' | 'agentProfilePic'>>;
	/** Open-ticket load per agent, shown beside each name in the assignee filter. */
	agentTicketCounts?: SupportAgentTicketCountMap;
	categoryMap: Record<string, { name: string; color?: string }>;
	/**
	 * Collapse behaviour for the desk, where the list shares the screen with an open thread. Absent
	 * wherever the list is the whole screen (phone, table view, no thread open) and always shown.
	 */
	collapse?: {
		/** Keeps the list open at full width; otherwise it sits on its rail until hovered. */
		isPinned: boolean;
		onTogglePin: () => void;
	};
}

export const TicketListPane: React.FC<TicketListPaneProps> = ({
	list,
	viewLabel,
	onBack,
	onNewTicket,
	selectedTicketId,
	onSelectTicket,
	navItems,
	categories,
	activeFilter,
	onSelectFilter,
	isSearching,
	searchValue,
	onSearchChange,
	filters,
	onFiltersChange,
	onClearFilters,
	teamMembers,
	agentTicketCounts,
	categoryMap,
	collapse,
}) => {
	const { tickets, isLoading, isError, totalCount, page: currentPage, totalPages, onSelectPage } = list;
	const prefetchTicket = usePrefetchSupportTicket();
	const isPhoneLayout = useMediaQuery({ maxWidth: 767 });
	const bulkUpdate = useBulkUpdateTicketsMutation();
	const now = new Date();
	const [selectionMode, setSelectionMode] = React.useState(false);
	const [selectedIds, setSelectedIds] = React.useState<ReadonlySet<string>>(() => new Set());
	const [selectionError, setSelectionError] = React.useState<string | null>(null);

	React.useEffect(() => {
		setSelectedIds(new Set());
		setSelectionMode(false);
		setSelectionError(null);
	}, [activeFilter, currentPage, filters, isSearching, searchValue]);

	const toggleSelection = (ticketId: string) => {
		setSelectedIds(previous => {
			const next = new Set(previous);
			if (next.has(ticketId)) next.delete(ticketId);
			else next.add(ticketId);
			return next;
		});
	};
	const closeSelectedTickets = () => {
		if (selectedIds.size === 0) return;
		bulkUpdate.mutate(
			{ ticketIds: [...selectedIds], update: { field: 'status', status: TicketStatus.Closed } },
			{
				onSuccess: () => {
					setSelectedIds(new Set());
					setSelectionMode(false);
					setSelectionError(null);
				},
				onError: error => setSelectionError(getApiErrorMessage(error, 'Failed to update tickets.')),
			}
		);
	};

	const countNoun = INACTIVE_VIEW_COUNT_NOUNS[activeFilter] ?? 'open';
	const countLabel = isSearching
		? `${totalCount} ${totalCount === 1 ? 'result' : 'results'}`
		: `${totalCount} ${countNoun}`;

	const filterChoices = React.useMemo(
		() => prepareInboxFilterChoices(categories, teamMembers, agentTicketCounts),
		[agentTicketCounts, categories, teamMembers]
	);

	// The search bar hides behind the header icon; it must stay visible while a refinement is
	// narrowing the list (including refinements set from the table view's header filters).
	const hasActiveRefinement = searchValue.trim().length > 0 || countActiveInboxFilters(filters) > 0;
	const [searchOpen, setSearchOpen] = React.useState(hasActiveRefinement);
	// Only a deliberate open takes the caret. The bar also expands on its own to show a refinement
	// carried in from the table, and focusing it then would swallow the desk's own `a` / `s` keys for
	// every ticket opened under a filter — the agent never asked to type.
	const [searchOpenedByAgent, setSearchOpenedByAgent] = React.useState(false);
	React.useEffect(() => {
		if (hasActiveRefinement) setSearchOpen(true);
	}, [hasActiveRefinement]);
	const toggleSearch = () => {
		if (searchOpen) {
			onSearchChange('');
			onClearFilters();
			setSearchOpen(false);
			setSearchOpenedByAgent(false);
		} else {
			setSearchOpen(true);
			setSearchOpenedByAgent(true);
		}
	};

	const isCollapsed = !!collapse && !collapse.isPinned;
	const peek = useTicketListPeek(isCollapsed);

	const pane = (
		<section
			className={css(
				s.listPane,
				...(isCollapsed ? [s.listPaneFloating] : []),
				...(isCollapsed && !peek.isPeeking ? [s.listPaneTucked] : [])
			)}
		>
			<div className={css(s.listHeader)}>
				<div className={css(s.listHeaderRow)}>
					<div className={css(s.listHeaderLeft)}>
						{/* Below 768px the desk layout is the only layout, so this cannot reach the table view. */}
						{!isPhoneLayout && (
							<Button type='button' variant='ghost' styles={[s.backBtn]} title='Back to inbox' onClick={onBack}>
								<ChevronLeft width={19} height={19} />
							</Button>
						)}
						<Dropdown>
							<DropdownTrigger render={<Button type='button' variant='ghost' styles={[s.listTitleBtn]} />}>
								<span className={css(s.listTitleGroup)}>
									<span className={css(s.listTitle)}>{viewLabel}</span>
									<ChevronDown className={css(s.switcherChevron)} width={14} height={14} strokeWidth={3} />
								</span>
							</DropdownTrigger>
							<DropdownContent align='start' styles={[s.viewMenu]}>
								{navItems.map(item => (
									<DropdownItem
										key={item.key}
										styles={activeFilter === item.key ? [s.viewMenuItem, s.viewMenuItemActive] : [s.viewMenuItem]}
										onClick={() => onSelectFilter(item.key)}
									>
										<span className={css(s.viewItemLabel)}>{item.label}</span>
										{item.count > 0 && <span className={css(s.viewCount)}>{item.count}</span>}
									</DropdownItem>
								))}
							</DropdownContent>
						</Dropdown>
						{isPhoneLayout && <span className={css(s.listInlineCount)}>{countLabel}</span>}
					</div>
					{isPhoneLayout && (
						<Button
							type='button'
							variant='ghost'
							styles={selectionMode ? [s.selectModeBtn, s.selectModeBtnActive] : [s.selectModeBtn]}
							aria-pressed={selectionMode}
							onClick={() => {
								setSelectionMode(value => !value);
								setSelectedIds(new Set());
								setSelectionError(null);
							}}
						>
							{selectionMode ? 'Done' : 'Select'}
						</Button>
					)}
					{collapse?.isPinned && (
						<Button
							type='button'
							variant='ghost'
							styles={[s.listHeaderIconBtn, s.listHeaderIconBtnActive]}
							title='Collapse ticket list'
							aria-label='Collapse ticket list'
							aria-pressed
							onClick={collapse.onTogglePin}
						>
							<SidebarGlyph size={16} />
						</Button>
					)}
					<Button
						type='button'
						variant='ghost'
						styles={searchOpen ? [s.listHeaderIconBtn, s.listHeaderIconBtnActive] : [s.listHeaderIconBtn]}
						title={searchOpen ? 'Close search' : 'Search tickets'}
						aria-label={searchOpen ? 'Close search' : 'Search tickets'}
						aria-expanded={searchOpen}
						onClick={toggleSearch}
					>
						<SearchGlyph size={16} />
					</Button>
				</div>
				{!isPhoneLayout && <span className={css(s.listSubCount)}>{countLabel}</span>}
			</div>

			{searchOpen && (
				<TicketSearchBar
					value={searchValue}
					onChange={onSearchChange}
					filters={filters}
					onFiltersChange={onFiltersChange}
					categoryOptions={filterChoices.categoryOptions}
					assigneeOptions={filterChoices.assigneeOptions}
					allowSort={!isSearching}
					onClearAll={onClearFilters}
					// A phone opens the field without the keyboard: it would cover the list the user is
					// about to filter, and Safari zooms the page when a small field takes focus.
					autoFocus={searchOpenedByAgent && !isPhoneLayout}
				/>
			)}

			<div className={css(s.listScroll)}>
				{isLoading && (
					<div className={css(s.loading)}>
						<LoadingSpinner type='small' />
					</div>
				)}
				{!isLoading && isError && <div className={css(s.empty)}>Failed to load tickets.</div>}
				{!isLoading && !isError && tickets.length === 0 && (
					<div className={css(s.empty)}>
						{isSearching ? 'No tickets match your search.' : 'No tickets in this view.'}
					</div>
				)}
				{!isLoading &&
					!isError &&
					tickets.map(ticket => {
						const selected = selectionMode ? selectedIds.has(ticket.id) : ticket.id === selectedTicketId;
						const status = getStatusInfo(ticket.status);
						const isActive = ticket.status === TicketStatus.Active;
						const isDimmed = isDimmedTicketStatus(ticket.status);
						const category = ticket.categoryId ? categoryMap[ticket.categoryId] : undefined;
						const contactLabel = getTicketContactLabel(ticket);
						return (
							<Button
								key={ticket.id}
								type='button'
								variant='ghost'
								styles={[
									s.ticketRow,
									...(selected ? [s.ticketRowActive] : []),
									...(isDimmed ? [s.ticketRowDimmed] : []),
								]}
								aria-pressed={selected}
								aria-label={selectionMode ? `Select ticket #${ticket.number}` : undefined}
								onClick={() => (selectionMode ? toggleSelection(ticket.id) : onSelectTicket(ticket.id))}
								onMouseEnter={() => prefetchTicket(ticket.id)}
								onFocus={() => prefetchTicket(ticket.id)}
							>
								<div className={css(s.rowTop)}>
									<Avatar
										imgSrc={getSupportContactProfilePicSrc(ticket)}
										altText={contactLabel === '—' ? 'Contact' : contactLabel}
										fallbackText={getInitials(ticket.contactName, ticket.contactEmail)}
										styles={[s.rowAvatar]}
									/>
									<span
										className={css(
											s.rowName,
											...(isActive ? [s.ticketActiveText] : []),
											...(isDimmed ? [s.ticketDimmedText] : [])
										)}
									>
										{contactLabel}
									</span>
									<span className={css(s.rowTime)}>{formatRelativeTime(ticket.lastMessageAt)}</span>
								</div>
								<div
									className={css(
										s.rowSubject,
										...(isActive ? [s.ticketActiveText] : []),
										...(isDimmed ? [s.ticketDimmedText] : [])
									)}
								>
									{ticket.subject}
								</div>
								{ticket.previewText && <div className={css(s.rowPreview)}>{ticket.previewText}</div>}
								<div className={css(s.rowPills)}>
									<Pill variant='tag' color={status.color} size='small' label={getStatusPillLabel(ticket, now)} />
									<TicketChannelBadge channel={ticket.channel} compact />
									{category && <Pill variant='category' color='gray' size='small' label={category.name} />}
								</div>
							</Button>
						);
					})}
			</div>
			{isPhoneLayout && selectionMode && (
				<div className={css(s.phoneSelectionBar)} role='toolbar' aria-label='Selected ticket actions'>
					<span>{selectedIds.size} selected</span>
					<Button
						type='button'
						variant='primary'
						disabled={selectedIds.size === 0 || bulkUpdate.isPending}
						onClick={closeSelectedTickets}
					>
						{bulkUpdate.isPending ? 'Closing…' : 'Close'}
					</Button>
					<Button type='button' variant='ghost' onClick={() => setSelectedIds(new Set())}>
						Clear
					</Button>
				</div>
			)}
			{isPhoneLayout && selectionError && <div className={css(s.selectionError)}>{selectionError}</div>}
			<TicketPagination page={currentPage} totalPages={totalPages} onSelectPage={onSelectPage} />
			{isPhoneLayout && onNewTicket && !selectionMode && (
				<NewTicketMenu
					onSelect={onNewTicket}
					render={
						<Button
							type='button'
							variant='primary'
							styles={[s.newTicketFab, ...(totalPages > 1 ? [s.newTicketFabAbovePagination] : [])]}
							aria-label='New ticket'
							title='New ticket'
						>
							<PlusIconThin fillColor='currentColor' width={16} height={16} />
						</Button>
					}
				/>
			)}
		</section>
	);

	if (!collapse || collapse.isPinned) return pane;

	return (
		<div ref={peek.regionRef} className={css(s.listRegion)} onMouseEnter={peek.openPeek} onMouseLeave={peek.closePeek}>
			<div className={css(s.listRail)}>
				<Button
					ref={peek.railRef}
					type='button'
					variant='ghost'
					styles={[s.listHeaderIconBtn]}
					title='Keep ticket list open'
					aria-label='Keep ticket list open'
					aria-pressed={false}
					onClick={collapse.onTogglePin}
				>
					<SidebarGlyph size={16} />
				</Button>
			</div>
			{pane}
		</div>
	);
};
